import { useCallback, useMemo, useRef, useState } from 'react';
import { RotateCw, Scaling } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PageHeader } from '@/components/PageHeader';
import { ResourceSwitch } from '@/components/ResourceSwitch';
import {
  ResourceList,
  type ResourceColumn,
  type ResourceFilterDef,
  type ResourceRowAction,
} from '@/components/resources/ResourceList';
import { ScaleServiceDialog } from '@/components/resources/ScaleServiceDialog';
import { api } from '@/lib/api';
import { formatAgo } from '@/lib/ago';
import type { ListQueryConfig } from '@/lib/listQuery';
import { useListQuery } from '@/lib/hooks/useListQuery';
import { usePersistedColumns } from '@/lib/hooks/usePersistedColumns';
import { usePolledData } from '@/lib/hooks/usePolledData';
import { useServerList } from '@/lib/hooks/useServerList';
import type { Service } from '@/types/stack';

// The services endpoint filters only by stack_id (plus name search).
const LIST_CONFIG: ListQueryConfig = {
  filterKeys: ['stack_id'],
  numericFilterKeys: ['stack_id'],
  sortableColumns: ['name', 'replicas', 'created_at'],
  defaultSortBy: 'created_at',
  defaultSortOrder: 'desc',
};

const SWITCH_OPTIONS = [
  { label: 'Stacks', to: '/resources/stacks' },
  { label: 'Services', to: '/resources/services' },
];

const fetchStacks = () => api.stacks.listAll();

const isSwarm = (s: Service) => s.stack?.type === 'swarm';
const SWARM_ONLY = 'Only available for Swarm services';

function createdText(iso: string): { ago: string; full: string } | null {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return { ago: formatAgo(t), full: new Date(t).toLocaleString() };
}

function portsText(s: Service): string {
  if (!Array.isArray(s.ports) || s.ports.length === 0) return '—';
  return s.ports.map((p) => `${p.external}:${p.internal}`).join(', ');
}

const COLUMNS: ResourceColumn<Service>[] = [
  { key: 'name', header: 'Name', sortable: true, title: (s) => s.name, cell: (s) => <span className="font-medium">{s.name}</span> },
  { key: 'image', header: 'Image', hideBelow: 'lg', title: (s) => s.image, cell: (s) => <span className="font-mono text-xs">{s.image}</span> },
  { key: 'stack', header: 'Stack', cell: (s) => s.stack?.name ?? '—' },
  {
    key: 'type', header: 'Type', hideBelow: 'lg',
    cell: (s) => <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs text-muted-foreground">{s.stack?.type ?? 'compose'}</span>,
  },
  { key: 'replicas', header: 'Replicas', sortable: true, cell: (s) => <span className="tabular-nums">{s.replicas}</span> },
  { key: 'ports', header: 'Ports', hideBelow: 'md', title: portsText, cell: (s) => <span className="font-mono text-xs">{portsText(s)}</span> },
  {
    key: 'created_at', header: 'Created', sortable: true, hideBelow: 'md',
    cell: (s) => {
      const t = createdText(s.created_at);
      return t ? <time dateTime={s.created_at} title={t.full}>{t.ago}</time> : '—';
    },
  },
];
const ALL_KEYS = COLUMNS.map((c) => c.key);

export function ServicesPage() {
  const { query, setSearch, setFilter, setSort, setPage, setLimit, clearFilters } = useListQuery(LIST_CONFIG, { limitStorageKey: 'services.limit' });
  const { rows, total, loading, refreshing, error, refetch } = useServerList<Service>('services', query);
  const stacksP = usePolledData(fetchStacks);
  const [visible, toggleColumn] = usePersistedColumns('services.columns', ALL_KEYS, ALL_KEYS);
  // Targets stay set while their dialog closes so the dialog content does not blank out mid-animation.
  const [scale, setScale] = useState<{ service: Service; open: boolean } | null>(null);
  const [restart, setRestart] = useState<{ service: Service; open: boolean } | null>(null);
  const [restarting, setRestarting] = useState<number | null>(null);
  const restartInFlight = useRef(false);

  const filters = useMemo<ResourceFilterDef[]>(
    () => [
      { key: 'stack_id', label: 'Stack', allLabel: 'All stacks', options: (stacksP.data ?? []).map((s) => ({ value: String(s.id), label: s.name })) },
    ],
    [stacksP.data],
  );

  const actions = useMemo<ResourceRowAction<Service>[]>(
    () => [
      {
        id: 'scale',
        label: (s) => `Scale ${s.name}`,
        icon: Scaling,
        enabled: isSwarm,
        disabledReason: () => SWARM_ONLY,
        run: (s) => setScale({ service: s, open: true }),
      },
      {
        id: 'restart',
        label: (s) => `Restart ${s.name}`,
        icon: RotateCw,
        enabled: isSwarm,
        disabledReason: () => SWARM_ONLY,
        busy: (s) => restarting === s.id,
        run: (s) => setRestart({ service: s, open: true }),
      },
    ],
    [restarting],
  );

  const onPage = useCallback((p: number) => setPage(p, total), [setPage, total]);

  const handleScale = async (replicas: number) => {
    if (!scale) return;
    await api.services.scale(scale.service.id, replicas); // a rejection is shown inline by the dialog
    toast.success('Service scaled');
    refetch();
  };

  const handleRestart = async () => {
    if (!restart || restartInFlight.current) return;
    const id = restart.service.id;
    restartInFlight.current = true;
    setRestarting(id);
    try {
      await api.services.restart(id);
      toast.success('Service restarted');
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : 'Failed to restart service');
    } finally {
      restartInFlight.current = false;
      setRestarting(null);
      setRestart((p) => (p && p.service.id === id ? { ...p, open: false } : p));
    }
    refetch(); // state may have changed even on failure
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Services" description="Swarm services running in your stacks." />
      <ResourceSwitch active="/resources/services" label="Stacks and Services" options={SWITCH_OPTIONS} />
      <ResourceList<Service>
        columns={COLUMNS}
        visibleColumns={visible}
        onToggleColumn={toggleColumn}
        rows={rows}
        total={total}
        getRowKey={(s) => s.id}
        query={query}
        onSearch={setSearch}
        onFilter={setFilter}
        onSort={setSort}
        onPage={onPage}
        onLimit={setLimit}
        onClearFilters={clearFilters}
        filters={filters}
        searchLabel="Search services"
        searchPlaceholder="Search by name"
        loading={loading}
        refreshing={refreshing}
        error={error}
        onRetry={refetch}
        tableLabel="Services"
        rowActions={actions}
        empty={{ title: 'No services yet', description: 'Services appear here after an instance syncs.' }}
      />
      {scale ? (
        <ScaleServiceDialog
          service={scale.service}
          open={scale.open}
          onClose={() => setScale((p) => (p ? { ...p, open: false } : p))}
          onScale={handleScale}
        />
      ) : null}
      {restart ? (
        <ConfirmDialog
          open={restart.open}
          onOpenChange={(open) => setRestart((p) => (p ? { ...p, open } : p))}
          title="Restart service?"
          description={`Restart '${restart.service.name}'? Its tasks will be recreated.`}
          confirmLabel="Restart"
          loading={restarting === restart.service.id}
          onConfirm={handleRestart}
        />
      ) : null}
    </div>
  );
}
